import React, {PureComponent} from "react"
import PropTypes from "prop-types"
import {get, isEqual, isEmpty} from "lodash"

import {getQuery} from "@irati/utils/history"
import {HasPermissionTo} from "@irati/utils/permissions"
import intercom from "@irati/utils/intercom"
import {getCurrentPosition} from "@irati/geolocation/src/helpers/devicePosition"
import notify from "@irati/utils/notify"
import id from "@irati/utils/id"
import {geolocationTracking} from "@irati-tracking/geolocation"
import {adjustPrecision, hasValidValues} from "@irati/geolocation/src/helpers/latLng"
import addQueryUtil from "../../utils/addQuery"

import withSearch from "../withSearch"
import {Container} from "./styled/SearchAllByLocation"
import Inputs from "../../components/SearchAllByLocation/Inputs"
import Locations from "../../components/SearchAllByLocation/Locations"

class SearchAllByLocation extends PureComponent {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  static propTypes = {
    handleNewSearchTermEntry: PropTypes.func.isRequired,
    handleNewGeoEntryByRadius: PropTypes.func.isRequired,
    handleNewGeoEntryByBoundingBox: PropTypes.func.isRequired,
    search: PropTypes.object.isRequired,
    boundingBox: PropTypes.array.isRequired,
    searchTerm: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number,
    radius: PropTypes.number
  }

  static defaultProps = {
    searchTerm: null,
    lat: null,
    lng: null,
    radius: null
  }

  constructor(props) {
    super(props)
    this.idNamespace = id("search-all-by-location", this.props, {force: true})
  }

  state = {
    userLatLng: null,
    mapCenterLatLng: null,
    geoInputLatLng: null,
    initialised: false,
    deals: [],
    boundingBox: []
  }

  componentDidMount = () => {
    this.init()
  }

  componentWillUnmount = () => {
    intercom.show()
  }

  componentDidUpdate() {
    const {search, boundingBox} = this.props
    const {deals: stateDeals, boundingBox: stateBoundingBox} = this.state
    const deals = get(search, "results.deals", [])
    /** Disabling lint because it is nested in a conditional */
    /* eslint-disable react/no-did-update-set-state */
    const newState = {}
    if (!isEqual(deals, stateDeals)) {
      newState.deals = deals
    }
    if (!isEqual(boundingBox, stateBoundingBox)) {
      newState.boundingBox = boundingBox
    }
    if (!isEmpty(newState)) {
      this.setState(newState)
    }
    /* eslint-enable react/no-did-update-set-state */
  }

  init = () => {
    const {geoInputDesc, boundsCenter} = getQuery()

    const {lat, lng} = this.props
    const isLatLngSet = hasValidValues(lat, lng)
    let latLng
    if (boundsCenter) {
      const [boundsCenterLat, boundsCenterLng] = boundsCenter.split(",")
      const parsedLat = parseFloat(boundsCenterLat)
      const parsedLng = parseFloat(boundsCenterLng)
      if (hasValidValues(parsedLat, parsedLng)) {
        latLng = {lat: parsedLat, lng: parsedLng}
      }
    } else if (isLatLngSet) {
      latLng = {lat, lng}
    }

    const doExistingParamsExist = latLng && geoInputDesc
    const newState = {initialised: true}
    if (doExistingParamsExist) {
      const adjustedLatLng = adjustPrecision(latLng)
      newState.mapCenterLatLng = adjustedLatLng
      newState.geoInputLatLng = adjustedLatLng
      this.updateUserLocation()
    }
    this.setState(newState)
    intercom.hide()
    geolocationTracking.viewSearchByLocation()
  }

  handleSearchInputChange = (searchTerm) => {
    this.props.handleNewSearchTermEntry(searchTerm)
  }

  handleGeoInputChange = (latLng) => {
    this.updateMapCenterLatLng(latLng)
    if (!this.state.userLatLng) {
      this.setState({
        userLatLng: latLng
      })
    }
    this.props.handleNewGeoEntryByRadius(latLng.lat, latLng.lng)
  }

  updateUserLocation = () => {
    return getCurrentPosition({useFallback: true})
      .then((pos) => {
        this.setState({
          userLatLng: pos.latLng
        })
        return pos
      })
      .catch(({message}) => notify.error({message}, this))
  }

  handleMapDataChange = (centerLatLng, mapBounds = null) => {
    this.updateMapCenterLatLng(centerLatLng)
    if (mapBounds) {
      const boundsCenter = `${centerLatLng.lat},${centerLatLng.lng}`
      addQueryUtil.addQuery({boundsCenter})
      const rect = [mapBounds.ne.lat, mapBounds.ne.lng, mapBounds.sw.lat, mapBounds.sw.lng]
      this.props.handleNewGeoEntryByBoundingBox(rect)
    }
  }

  handleMapCenterChange = (mapCenterLatLng) => {
    this.updateMapCenterLatLng(mapCenterLatLng)
  }

  updateMapCenterLatLng = (mapCenterLatLng) => {
    const adjustedMapCenterLatLng = adjustPrecision(mapCenterLatLng)
    if (!isEqual(adjustedMapCenterLatLng, this.state.mapCenterLatLng)) {
      this.setState({
        mapCenterLatLng: adjustedMapCenterLatLng
      })
    }
  }

  render() {
    const {searchTerm, search} = this.props
    const {initialised, mapCenterLatLng, geoInputLatLng, userLatLng, deals, boundingBox} = this.state
    if (!initialised) {
      return null
    }

    return (
      <HasPermissionTo permissions="platform.geoLocation">
        <Container>
          {!mapCenterLatLng && <div className="modal-backdrop fade in" />}
          <Inputs
            geoInputLatLng={geoInputLatLng}
            userLatLng={userLatLng}
            searchTerm={searchTerm}
            onSearchInputChange={this.handleSearchInputChange}
            onGeoInputChange={this.handleGeoInputChange} />
          <Locations
            id={this.idNamespace}
            mapCenterLatLng={mapCenterLatLng}
            userLatLng={userLatLng}
            onMapDataChange={this.handleMapDataChange}
            deals={deals}
            load={search.load}
            boundingBox={boundingBox}
            searchTerm={searchTerm}
            onMapCenterChange={this.handleMapCenterChange} />
        </Container>
      </HasPermissionTo>
    )
  }
}

export default withSearch(SearchAllByLocation, true)
